"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";

import { useLocale, useTranslations } from "next-intl";

import { fetchUserMe, loginWithGoogle } from "./api";
import { useToast } from "./toast";
import { GOOGLE_CLIENT_ID, loadGoogleIdentity, preloadGoogleIdentity } from "./google";
import type { UserProfile } from "./types";

type AuthContextValue = {
  isLoggedIn: boolean;
  token: string;
  authEmail: string;
  user: UserProfile | null;
  ready: boolean;
  openLogin: () => void;
  closeLogin: () => void;
  logout: () => void;
  // 重新拉取当前用户资料（含套餐剩余分钟）。创建任务/重新成片会扣减额度，
  // 调用方在成功后应调用它以让顶栏「剩余分钟」立即刷新。
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth 必须在 AuthProvider 内部使用");
  }
  return ctx;
}

const API_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL ?? "http://localhost:8080";

export function AuthProvider({ children }: { children: ReactNode }) {
  const { notify } = useToast();
  const t = useTranslations("auth");
  const locale = useLocale();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState("");
  const [authEmail, setAuthEmail] = useState("");
  const [user, setUser] = useState<UserProfile | null>(null);
  const [ready, setReady] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Google 按钮渲染状态：loading（占位等待）→ ready（按钮已渲染）/
  // unavailable（无法访问 Google 或渲染失败）→ 收起占位并显示内联提示。
  const [googleState, setGoogleState] = useState<"loading" | "ready" | "unavailable">("loading");
  const googleButtonRef = useRef<HTMLDivElement>(null);

  // 关闭弹窗时重置表单状态
  const closeModal = useCallback(() => {
    setShowModal(false);
    setPassword("");
    setShowPassword(false);
    setSubmitting(false);
  }, []);

  useEffect(() => {
    const savedToken = localStorage.getItem("cg_token");
    const savedEmail = localStorage.getItem("cg_email");
    if (savedToken && savedEmail) {
      setToken(savedToken);
      setAuthEmail(savedEmail);
      setIsLoggedIn(true);
    }
    setReady(true);
  }, []);

  // 受保护页面跳转过来的登录入口（/?login=1）：本地会话恢复完且仍未登录时，
  // 自动打开登录弹窗，并清理 URL 参数避免刷新后再次弹出。
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("login") !== "1") return;
    if (!ready) return;
    if (!isLoggedIn) setShowModal(true);
    params.delete("login");
    const qs = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
  }, [ready, isLoggedIn]);

  // 提前预热 GIS 脚本：页面空闲时即下载并缓存，等用户打开登录弹窗时按钮近乎秒出，
  // 避免「弹窗先空着、过一会儿 Google 按钮才冒出来」的观感。
  useEffect(() => {
    const run = () => preloadGoogleIdentity();
    const w = window as any;
    if (typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(run, { timeout: 2000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const t = window.setTimeout(run, 1000);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (isLoggedIn && token) {
      fetchUserMe(token)
        .then(setUser)
        .catch(() => {
          setUser(null);
          logout();
        });
    } else {
      setUser(null);
    }
  }, [isLoggedIn, token]);

  // Google 登录：走 GIS 官方「按钮流程」（renderButton + OAuth 弹窗）。
  // 切勿改回 google.accounts.id.prompt()（One Tap）：该流程依赖浏览器的 FedCM /
  // 「第三方登录」开关，被禁用时会直接抛
  // 「FedCM get() rejects with NetworkError: Error retrieving a token」，
  // 且其 isNotDisplayed()/isSkippedMoment() 状态方法已被 Google 废弃。
  useEffect(() => {
    if (!showModal || !GOOGLE_CLIENT_ID) return;
    setGoogleState("loading");
    let canceled = false;
    let blankTimer: number | undefined;
    let onResize: (() => void) | null = null;
    let lastWidth = 0;

    // GIS 的 width 只接受像素值：按容器实宽取整并夹到 [200,320]，避免窄屏溢出。
    const renderOfficialButton = (google: any) => {
      const container = googleButtonRef.current;
      if (!container) return;
      const width = Math.max(200, Math.min(320, Math.round(container.clientWidth) || 320));
      if (width === lastWidth) return;
      lastWidth = width;
      container.innerHTML = "";
      google.accounts.id.renderButton(container, {
        type: "standard",
        theme: "outline",
        size: "large",
        shape: "pill",
        text: "signin_with",
        logo_alignment: "left",
        width,
        // 跟随站点语言（GIS 不指定 locale 会跟随浏览器/Google 账号语言）
        locale: locale === "zh" ? "zh_CN" : "en",
      });
    };

    loadGoogleIdentity()
      .then((google) => {
        if (canceled) return;
        google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (resp: { credential?: string }) => {
            if (resp.credential) void handleGoogleLogin(resp.credential);
          },
          ux_mode: "popup",
        });
        renderOfficialButton(google);
        // 官方按钮已成功渲染到容器：切换状态以隐藏骨架占位并露出分隔线
        setGoogleState("ready");
        onResize = () => renderOfficialButton(google);
        window.addEventListener("resize", onResize);
        // 渲染失败（如当前域名未加入 Google 控制台授权来源）时按钮区会一片空白，
        // 兜底提示并收起占位区域，避免弹窗中部留一大块空白。
        blankTimer = window.setTimeout(() => {
          if (!canceled && !googleButtonRef.current?.childElementCount) {
            setGoogleState("unavailable");
            notify(t("googleButtonFailed"), "error");
          }
        }, 2500);
      })
      .catch(() => {
        if (!canceled) {
          setGoogleState("unavailable");
          notify(t("googleLoginFailed"), "error");
        }
      });

    return () => {
      canceled = true;
      if (blankTimer) window.clearTimeout(blankTimer);
      if (onResize) window.removeEventListener("resize", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, locale]);

  // 把后端返回的稳定错误码映射成本地化提示（中文/英文随站点语言）。
  function authErrorMessage(e: unknown): string {
    const code = e instanceof Error ? e.message : "";
    switch (code) {
      case "invalid_email":
        return t("invalidEmail");
      case "weak_password":
        return t("weakPassword");
      case "email_taken":
        return t("emailTaken");
      default:
        return t("authFailed");
    }
  }

  async function handleAuth(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    const endpoint = isRegistering ? "/auth/register" : "/auth/login";
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: authEmail, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t("authFailed"));

      setToken(data.token);
      setIsLoggedIn(true);
      localStorage.setItem("cg_token", data.token);
      localStorage.setItem("cg_email", authEmail);
      try {
        setUser(await fetchUserMe(data.token));
      } catch {
        // 忽略：用户资料稍后可在详情页补充
      }
      closeModal();
      notify(isRegistering ? t("registerSuccess") : t("loginSuccess"));
    } catch (err) {
      notify(authErrorMessage(err), "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleGoogleLogin(credential: string) {
    try {
      const data = await loginWithGoogle(credential);
      setToken(data.token);
      setIsLoggedIn(true);
      localStorage.setItem("cg_token", data.token);
      localStorage.setItem("cg_email", data.user.email);
      setAuthEmail(data.user.email);
      setUser(data.user);
      closeModal();
      notify(t("googleLoginSuccess"));
    } catch (err) {
      notify(err instanceof Error ? err.message : t("googleLoginFailed"), "error");
    }
  }

  function logout() {
    setIsLoggedIn(false);
    setToken("");
    setUser(null);
    setAuthEmail("");
    localStorage.removeItem("cg_token");
    localStorage.removeItem("cg_email");
  }

  // refreshUser 用于在扣减额度的动作（创建任务 / 重新成片）成功后主动刷新
  // 顶栏的「剩余分钟」。静默失败：保留旧资料，避免网络抖动让额度显示闪没。
  const refreshUser = useCallback(async () => {
    const effectiveToken =
      token || (typeof window !== "undefined" ? localStorage.getItem("cg_token") || "" : "");
    if (!effectiveToken) return;
    try {
      setUser(await fetchUserMe(effectiveToken));
    } catch {
      // 忽略：稍后页面刷新会重新拉取
    }
  }, [token]);

  const value: AuthContextValue = {
    isLoggedIn,
    token,
    authEmail,
    user,
    ready,
    openLogin: () => setShowModal(true),
    closeLogin: closeModal,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {showModal ? (
        <div className="cgOverlay" onClick={closeModal}>
          <div className="cgAuthModal" onClick={(e) => e.stopPropagation()}>
            {/* 关闭按钮 */}
            <button className="cgAuthClose" onClick={closeModal} type="button" aria-label={t("close")}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            </button>

            {/* 品牌标识 */}
            <div className="cgAuthBrand">
              <div className="cgAuthBrandMark">C</div>
              <span className="cgAuthBrandName">ClipGlobe</span>
            </div>

            <div className="cgAuthHeader">
              <h2 className="cgAuthTitle">
                {isRegistering ? t("registerTitle") : t("loginTitle")}
              </h2>
              <p className="cgHint">
                {isRegistering ? t("registerSub") : t("loginSub")}
              </p>
            </div>

            {/* 社交登录区：Google 不可用时收起占位与分隔线，仅显示一行内联提示 */}
            {GOOGLE_CLIENT_ID ? (
              googleState === "unavailable" ? (
                <p className="cgGoogleFallback">{t("googleUnavailable")}</p>
              ) : (
                <>
                  {/* 官方 Google 按钮由 GIS 动态渲染到此容器（见上方 useEffect）。
                      脚本仍在下载时显示骨架占位，按钮就位后立即替换为官方按钮 */}
                  <div className="cgGoogleWrap">
                    <div ref={googleButtonRef} className="cgGoogleSignIn" />
                    {googleState === "loading" && (
                      <div className="cgGoogleSkeleton" aria-hidden="true">
                        <span className="cgGoogleSkeletonIcon" />
                        <span className="cgGoogleSkeletonText">{t("continueWithGoogle")}</span>
                      </div>
                    )}
                  </div>
                  {googleState === "ready" ? (
                    <div className="cgDividerWithText">
                      <span className="cgDividerLine" />
                      <span className="cgDividerText">{t("orDivider")}</span>
                      <span className="cgDividerLine" />
                    </div>
                  ) : null}
                </>
              )
            ) : null}

            {/* 邮箱密码表单 */}
            <form className="cgForm" onSubmit={handleAuth}>
              <div className="cgField">
                <label className="cgLabel" htmlFor="auth-email">{t("emailLabel")}</label>
                <div className="cgInputWrap">
                  <svg className="cgInputIcon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 4L12 13 2 4"/>
                  </svg>
                  <input
                    id="auth-email"
                    type="email"
                    className="cgControl cgControlWithIcon"
                    placeholder={t("emailPlaceholder")}
                    value={authEmail}
                    onChange={(e) => setAuthEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>
              </div>
              <div className="cgField">
                <label className="cgLabel" htmlFor="auth-password">{t("passwordLabel")}</label>
                <div className="cgInputWrap">
                  <svg className="cgInputIcon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                  </svg>
                  <input
                    id="auth-password"
                    type={showPassword ? "text" : "password"}
                    className="cgControl cgControlWithIcon"
                    placeholder={t("passwordPlaceholder")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete={isRegistering ? "new-password" : "current-password"}
                  />
                  <button
                    type="button"
                    className="cgPasswordToggle"
                    onClick={() => setShowPassword((v) => !v)}
                    tabIndex={-1}
                    aria-label={showPassword ? t("hidePassword") : t("showPassword")}
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <button className="cgBtn cgBtnPrimary cgBtnSubmit" type="submit" disabled={submitting}>
                {submitting ? (
                  <span className="cgSpinner" />
                ) : isRegistering ? t("submitRegister") : t("submitLogin")}
              </button>
            </form>

            {/* 底部切换：纯文本链接风格 */}
            <p className="cgAuthSwitch">
              {isRegistering ? t("haveAccount") : t("noAccount")}{" "}
              <button
                type="button"
                className="cgAuthSwitchLink"
                onClick={() => setIsRegistering((prev) => !prev)}
              >
                {isRegistering ? t("switchToLogin") : t("switchToRegister")}
              </button>
            </p>
          </div>
        </div>
      ) : null}
    </AuthContext.Provider>
  );
}
